/*
 * hp-subscription — the PDP subscribe / one-time picker (Figma 9120:9389).
 *
 * Deliberately small. The CART CONTRACT is a single form-associated input
 * (`name="selling_plan"`), so this file only has to keep that input, the
 * selected-state classes and the derived copy in step. Everything it prices
 * from was computed by Shopify in Liquid — see the JSON payload in
 * snippets/hp-subscription.liquid — so there is no money formatting here and
 * no re-derivation of a discount that the cart would then disagree with.
 *
 * Variant sync uses the SAME hook Skio's own component uses: a `change` event
 * on the product form's `[name="id"]` input, which product-info.js:261
 * dispatches with `bubbles: true` after every variant switch. Listening on the
 * form (not the input) means the listener survives that input being replaced.
 */
class HpSubscription extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('[data-hp-sub-data]');
    if (!payload) return;

    try {
      this.data = JSON.parse(payload.textContent);
    } catch (e) {
      // A malformed payload must not take the Add to Cart button down with it:
      // leaving the server-rendered markup exactly as it is still adds the
      // default plan to the cart correctly.
      return;
    }

    this.input = this.querySelector('[data-hp-sub-selling-plan]');
    this.form = document.getElementById(this.dataset.form);
    this.freq = this.querySelector('[data-hp-sub-freq]');
    this.badge = this.querySelector('[data-hp-sub-badge]');
    this.note = this.querySelector('[data-hp-sub-note-save]');
    this.modes = Array.from(this.querySelectorAll('[data-hp-sub-mode]'));
    this.plans = Array.from(this.querySelectorAll('.hp-sub__plan'));

    // The Add to Cart button's price, rendered by snippets/buy-buttons.liquid.
    // Found through the form rather than by id so this keeps working if the
    // section id or the block order changes, and stays null when the merchant
    // has "Show price in the Add to Cart button" switched off.
    this.ctaPrice = this.form?.querySelector('.product-form__submit-price') || null;

    /*
     * THE PRICE UNDER THE TITLE. Base's `price` snippet renders it, and nothing
     * was keeping it in step with the picker — the CTA read "Add to Cart –
     * $29.75" while the headline price two blocks above still said $35.00, so
     * the page quoted two different numbers for the same purchase.
     *
     * Found by OUR class, not by the base id. `#price-{sectionId}` would need
     * the section id threaded into the snippet, and the price is an optional
     * block a merchant can remove — `.hp-pdp__price` is unique within the
     * section and simply returns null when the block is absent.
     */
    this.mainPrice = this.closest('.hp-pdp, .shopify-section')?.querySelector('.hp-pdp__price') || null;

    this.dialog = this.querySelector('[data-hp-sub-help]');
    if (this.dialog) this.bindHelp();

    this.variantId = this.form?.querySelector('[name="id"]')?.value || Object.keys(this.data.variants)[0];

    this.addEventListener('change', this.onChange);

    // Bound on the FORM so it keeps working after product-info.js replaces the
    // variant input's markup on a variant switch.
    if (this.form) {
      this.onVariantChange = this.onVariantChange.bind(this);
      this.form.addEventListener('change', this.onVariantChange);
    }

    /*
     * AND AGAIN AFTER THE SWAP. `onVariantChange` above fires on the form's
     * `change`, which is BEFORE product-info.js has fetched and replaced
     * `#price-{sectionId}` with the server's markup — and that markup is always
     * the one-time price. Re-pricing at that moment writes the right number
     * into a node about to be thrown away.
     *
     * `variantChange` is published at the END of renderProductInfo, after the
     * swap, so it is the only safe moment. Guarded on our own section id so a
     * second product-info on the page (a quick view) cannot re-price this one.
     */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.variant?.id) this.variantId = String(event.data.variant.id);
        this.render();
      });
    }

    this.render();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.onVariantChange);
    this.variantChangeUnsubscriber?.();
  }

  /*
   * showModal() rather than show(): it is the one that puts the dialog in the
   * top layer, draws ::backdrop, contains focus and closes on Escape. Nothing
   * here reimplements any of that. The backdrop click is the only gap — a
   * click on the backdrop reports the DIALOG as its target, so it is told apart
   * from a click on the panel by hit-testing the panel's own box.
   */
  bindHelp() {
    // showModal() only. The `open` attribute was the obvious fallback and is
    // WORSE than nothing: without <dialog> support the element renders inline in
    // the flow with no backdrop and no way out, which is a broken panel rather
    // than a missing one. <dialog> has been baseline since Safari 15.4 (2022).
    this.querySelector('[data-hp-sub-help-open]')?.addEventListener('click', () => {
      this.dialog.showModal();
    });

    this.querySelector('[data-hp-sub-help-close]')?.addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      const inside =
        event.clientY >= box.top &&
        event.clientY <= box.bottom &&
        event.clientX >= box.left &&
        event.clientX <= box.right;
      if (!inside) this.dialog.close();
    });
  }

  onChange = (event) => {
    if (!event.target.matches('.hp-sub__native')) return;
    this.render();
  };

  onVariantChange(event) {
    if (!event.target.matches('[name="id"]')) return;
    this.variantId = event.target.value;
    this.render();
  }

  get mode() {
    return this.querySelector('[name^="hp-sub-mode-"]:checked')?.value || 'subscribe';
  }

  get planId() {
    return this.querySelector('[name^="hp-sub-plan-"]:checked')?.value || this.data.plans[0];
  }

  render() {
    const variant = this.data.variants[this.variantId];
    const mode = this.mode;
    const planId = this.planId;

    // A variant with no allocation for this plan (Skio can scope a plan to some
    // variants only) is treated as one-time for pricing, and the subscribe card
    // simply shows the full price rather than a wrong one.
    const plan = variant?.plans?.[planId];

    this.modes.forEach((el) => el.classList.toggle('is-selected', el.dataset.hpSubMode === mode));
    this.plans.forEach((el) => el.classList.toggle('is-selected', el.querySelector('.hp-sub__native')?.checked));

    // THE ONLY LINE THE CART CARES ABOUT. Empty value = a one-time purchase,
    // which is exactly what Skio's own updateForm() writes.
    if (this.input) this.input.value = mode === 'subscribe' && plan ? planId : '';

    if (this.freq) this.freq.hidden = mode !== 'subscribe';

    if (variant) {
      this.setAmount('onetime', variant.amount);
      this.setAmount('subscribe', plan ? plan.amount : variant.amount);
    }

    /*
     * The headline price reads the SAME allocation as the cards and the CTA, so
     * all three state one number. Taken from the payload rather than by reading
     * and restoring the DOM's own text: once this has written a subscription
     * price into the node, the node no longer knows what the one-time price was,
     * and a stashed copy goes stale the moment product-info swaps the block.
     */
    if (variant) this.setMainPrice(mode === 'subscribe' && plan ? plan.money : variant.money);

    // The CTA reads the SAME allocation the cards do, so "Add to Cart – $29.75"
    // can never disagree with the price beside Subscribe & Save. Left alone when
    // the button is empty, which is how buy-buttons renders a sold-out variant.
    if (this.ctaPrice && this.ctaPrice.textContent.trim() !== '') {
      const money = mode === 'subscribe' && plan ? plan.money : variant?.money;
      if (money) this.ctaPrice.textContent = `\u00A0\u2013\u00A0${money}`;
    }

    if (this.badge) {
      this.badge.textContent = plan && plan.percent > 0 ? `${plan.percent}% Off` : '';
    }

    if (this.note) {
      const template = this.dataset.saveTemplate || '';
      this.note.textContent =
        plan && plan.saving > 0
          ? template.replace('[savings]', `${this.dataset.symbol || ''}${plan.savingAmount}`)
          : '';
    }
  }

  /*
   * Writes into whichever of base's two price blocks is the live one.
   *
   * `price.liquid` renders BOTH `.price__regular` and `.price__sale` and lets
   * `price--on-sale` on the wrapper decide which is shown, so "the visible
   * price" is a class test, not a DOM search — querying for the sale node first
   * would find a hidden element on every product that is not discounted.
   *
   * Only the LIVE number is replaced. On a discounted product the struck
   * compare-at stays as it is: it is the retail price this variant is marked
   * down from, which does not change because the shopper chose to subscribe.
   */
  setMainPrice(money) {
    if (!this.mainPrice || !money) return;

    const wrapper = this.mainPrice.querySelector('.price');
    if (!wrapper) return;

    const onSale = wrapper.classList.contains('price--on-sale');
    const node = onSale
      ? wrapper.querySelector('.price__sale .price-item--sale bdi')
      : wrapper.querySelector('.price__regular .price-item--regular bdi');

    if (node) node.textContent = money;
  }

  setAmount(which, amount) {
    const el = this.querySelector(`[data-hp-sub-amount="${which}"]`);
    if (el && amount != null) el.textContent = amount;
  }
}

if (!customElements.get('hp-subscription')) {
  customElements.define('hp-subscription', HpSubscription);
}
